import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRest } from "@/composables/index";

const emptyForm = () => ({
  tipoAfectacion: null,
  fecha: "",
  monto: "",
  moneda: "",
  origen: "",
  destino: "",
  entidad: "",
  afectado: "",
  vinculado: "",
  propietario: "",
  observaciones: "",
  idCoelsa: "",
  nroTarjeta: "",
  propietarioNombre: "",
  propietarioDNI: ""
});

const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

export const useAfectacionStore = defineStore("AfectacionPatrimonial", () => {
  const selected = ref(null);
  const form = ref(emptyForm());
  const formType = ref("view");
  const afectacionesPatrimoniales = ref([]);
  const actuacionID = ref("");
  const rest = useRest();
  const isLoading = ref(false);
  const baseUrl = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/afectacion-patrimonial`;
  const isFormValid = ref(false);
  const validationErrors = ref({});

  const fetchAfectaciones = async (id) => {
    actuacionID.value = id;
    try {
      const url = `${baseUrl}?search={"ActuacionID": [{"operator": "=", "value": "${id}"}]}`;
      const resp = await rest.get(url);
      afectacionesPatrimoniales.value = resp.data.data;

      const idCoelsa = await handleFetchCoelsa(id);

      afectacionesPatrimoniales.value.forEach((afectacion) => {
        afectacion.codigoCoelsa = idCoelsa;
      });
    } catch (err) {
      console.error("Error al traer afectaciones patrimoniales", err);
    }
  };
  
  const validateForm = () => {
    validationErrors.value = {};
    const errors = {};
  
    const camposObligatorios = [
      "tipoAfectacion",
      "fecha",
      "monto",
      "moneda",
      "observaciones",
    ];
  
    camposObligatorios.forEach((campo) => {
      if (isEmpty(form.value[campo])) {
        errors[campo] = "Campo obligatorio";
      }
    });
  
    // validaciones condicionales
    const tipo = form.value.tipoAfectacion?.value;
  
    if (tipo === "transferencia") {
      if (isEmpty(form.value.destino)) {
        errors.destino = "Campo obligatorio para transferencias";
      }
      if (isEmpty(form.value.origen)) {
        errors.origen = "Campo obligatorio para transferencias";
      } else if (form.value.origen.trim().length < 3) {
        errors.origen = "Debe tener al menos 3 caracteres";
      }
      if (isEmpty(form.value.entidad)) {
        errors.entidad = "Campo obligatorio para transferencias";
      } else if (form.value.entidad.trim().length < 3) {
        errors.entidad = "Debe tener al menos 3 caracteres";
      }
    }

    if (tipo === "credito" || tipo === "debito") {
      if (isEmpty(form.value.nroTarjeta)) {
        errors.nroTarjeta = "Número de tarjeta obligatorio";
      } else if (!/^\d+$/.test(form.value.nroTarjeta)) {
        errors.nroTarjeta = "El número de tarjeta debe ser numérico";
      } else if (form.value.nroTarjeta.length < 5) {
        errors.nroTarjeta = "Debe tener al menos 5 dígitos";
      }
      if (isEmpty(form.value.entidad)) {
        errors.entidad = "Campo obligatorio";
      } else if (form.value.entidad.trim().length < 3) {
        errors.entidad = "Debe tener al menos 3 caracteres";
      }
    }

    if (tipo === "transferencia" && form.value?.propietario?.id === 0) {
      if (isEmpty(form.value?.propietarioNombre)) {
        errors.propietarioNombre = "Campo obligatorio";
      }
      if (isEmpty(form.value?.propietarioDNI)) {
        errors.propietarioDNI = "DNI obligatorio";
      } else if (!/^\d+$/.test(form.value.propietarioDNI)) {
        errors.propietarioDNI = "El DNI debe ser numérico";
      } else if (form.value.propietarioDNI.length < 5) {
        errors.propietarioDNI = "Debe tener al menos 5 dígitos";
      }
    }

  
    validationErrors.value = errors;
    isFormValid.value = Object.keys(errors).length === 0;
    
    return isFormValid.value;
  };
  
  const handlePutAfectaciones = async () => {

    if (!validateForm()) return;
    isLoading.value = true;

    const formatedAfectacion = {
      ActuacionID: "",
      AfectacionPatrimonialID: form.value.AfectacionPatrimonialID,
      Afectado: form.value.afectado,
      BienEntregadoID: "",
      Descripcion: form.value.observaciones,
      Dni: "",
      EntidadBancaria: form.value.entidad,
      FechaCreacion: form.value.fecha,
      FechaEliminacion: "",
      FechaTransaccion: "",
      IdentificadorFinancieroOrigen: form.value.origen,
      IdentificadorFinancieroDestino: form.value.destino,
      Monto: form.value.monto,
      NombreCompleto: "",
      NumeroTarjeta: form.value.nroTarjeta,
      PropietarioCuenta: form.value.propietario,
      TipoAfectacionPatrimonial: "",
      TipoMonedaID: form.value.moneda.id,
      Vinculado: form.value.vinculado,
      codigoCoelsa: form.value.idCoelsa,
      propietarioNombre: form.value.propietarioNombre,
      propietarioDNI: form.value.propietarioDNI
    };
    if (isEdit.value) {
      afectacionesPatrimoniales.value = afectacionesPatrimoniales.value.map((afectacion) => {
        if (afectacion?.AfectacionPatrimonialID === form?.value?.AfectacionPatrimonialID) {
          return {
            ...afectacion,
            ...formatedAfectacion,
          };
        }
        return {
          ...afectacion,
        };
      });
      await handleSave();
    } else if (isCreate.value) {
      handleCreateAfectacion(formatedAfectacion);
    }
  }
  
  const handleSaveLocally = async () => {
    if (!validateForm()) return;
    isLoading.value = true;
  
    const formatedAfectacion = {
      ActuacionID: "",
      AfectacionPatrimonialID: form.value?.AfectacionPatrimonialID || `temp-${Date.now()}`,
      Afectado: form.value.afectado,
      BienEntregadoID: "",
      Descripcion: form.value.observaciones,
      Dni: "",
      EntidadBancaria: form.value.entidad,
      FechaCreacion: form.value.fecha,
      FechaEliminacion: "",
      FechaTransaccion: "",
      IdentificadorFinancieroOrigen: form.value.origen,
      IdentificadorFinancieroDestino: form.value.destino,
      Monto: form.value.monto,
      NombreCompleto: "",
      NumeroTarjeta: form.value.nroTarjeta,
      PropietarioCuenta: form.value.propietario,
      TipoAfectacionPatrimonial: form.value.tipoAfectacion,
      Moneda: form.value.moneda,
      Vinculado: form.value.vinculado,
      codigoCoelsa: form.value.idCoelsa,
      PropietarioNombre: form.value.propietarioNombre,
      PropietarioDNI: form.value.propietarioDNI
    };
  
    if (isCreate.value) {
      afectacionesPatrimoniales.value.push(formatedAfectacion);
    } else if (isEdit.value) {
      const index = afectacionesPatrimoniales.value.findIndex((v) => {
        return v.AfectacionPatrimonialID === form.value.AfectacionPatrimonialID;
      });
      if (index !== -1) {
        afectacionesPatrimoniales.value[index] = formatedAfectacion;
      }
    }
  
    isLoading.value = false;
    startView();
  };
  
  const handleDeleteAfectacion = async (locally = false) => {
    isLoading.value = true;
    try {
      const selectedIndexArray = afectacionesPatrimoniales.value.findIndex(
        (item) => item.AfectacionPatrimonialID === selectedId.value
      );
      afectacionesPatrimoniales.value = afectacionesPatrimoniales.value.filter(
        (afectacion) => afectacion.AfectacionPatrimonialID !== selectedId.value
      );
      if (!locally) await handleSave(selectedIndexArray);
    } catch (err) {
      console.error("Error al eliminar afectación", err);
      isLoading.value = false;
      throw err;
    }
  };
  
  const handleCreateAfectacion = async (newAfectacion) => {
    afectacionesPatrimoniales.value = [
      ...afectacionesPatrimoniales.value,
      {
        ...newAfectacion,
      },
    ];
    await handleSave();
  };
  
  const handleSave = async (indexToRedirect) => {
    try {
      const formatedAfectacionList = afectacionesPatrimoniales.value.map((afectacion) => ({
        ...afectacion
      }));

      let options = {
        url: `${baseUrl}/${actuacionID.value}`,
        formData: { Afectaciones: formatedAfectacionList },
        ContentType: "text/plain",
      };
      await rest.put(options);
    } catch (err) {
      console.error("Error al editar afectaciones", err);
    } finally {
      isLoading.value = false;
      if (isCreate.value || isEdit.value) triggerSuccessModal();
      startView();
      if (indexToRedirect !== undefined) clear();
      fetchAfectaciones(actuacionID.value, indexToRedirect);
    }
  };
  
  const select = (afectacion) => {
    selected.value = afectacion;
    hydrateFormFromDoc(afectacion);
    validationErrors.value = {};
    formType.value = "view";
  };

  const startCreate = () => {
    selected.value = null;
    form.value = emptyForm();
    validationErrors.value = {};
    formType.value = "create";
  };
  
  const hydrateFormFromDoc = (afectacion) => {
    form.value = {
      AfectacionPatrimonialID: afectacion?.AfectacionPatrimonialID,
      tipoAfectacion: afectacion?.TipoAfectacionPatrimonial || "",
      fecha: afectacion?.FechaCreacion || "",
      monto: afectacion?.Monto || "",
      moneda: afectacion?.Moneda || "",
      origen: afectacion?.IdentificadorFinancieroOrigen || "",
      destino: afectacion?.IdentificadorFinancieroDestino || "",
      entidad: afectacion?.EntidadBancaria || "",
      afectado: afectacion?.Afectado || "",
      vinculado: afectacion?.Vinculado || "",
      propietario: afectacion?.PropietarioCuenta || "",
      observaciones: afectacion?.Descripcion || "",
      idCoelsa: afectacion?.codigoCoelsa || "",
      nroTarjeta: afectacion?.NumeroTarjeta || "",
      propietarioNombre: afectacion?.PropietarioNombre || "",
      propietarioDNI: afectacion?.PropietarioDNI || "",
    };
  };
  
  const startEdit = () => {
    formType.value = "edit";
  };
  const startView = () => {
    formType.value = "view";
  };

  const resetForm = () => {
    form.value = emptyForm();
  };

  const clear = () => {
    selected.value = null;
    resetForm();
  };

  const selectFirst = () => {
    if (afectacionesPatrimoniales.value?.length) {
      select(afectacionesPatrimoniales.value[0]);
    }
  };
  
  const selectedId = computed(() => selected.value?.AfectacionPatrimonialID ?? null);
    
  const isSelected = (id) => selected.value?.AfectacionPatrimonialID === id;
  const isView = computed(() => formType.value === "view");
  const isEdit = computed(() => formType.value === "edit");
  const isCreate = computed(() => formType.value === "create");
  const inputsDisabled = computed(() => formType.value === "view");

  const showSuccessModal = ref();
  
  const setShowSuccessModal = (fn) => {
    showSuccessModal.value = fn;
  };

  const triggerSuccessModal = () => {
    showSuccessModal.value?.();
  };

  const setValidationErrors = (errors) => {
    validationErrors.value = errors;
  };
    
  const handleFetchCoelsa = async (actuacionID) => {
    const { data } = await rest.get({
      url: `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/persona-fisica-coelsa?search={"exp_pre_ActuacionPersonaFisica.ActuacionID": [{"operator": "=", "value": "${actuacionID}"}]}`,
    });
    if (!data.data.length) return "Sin registros";
    return data.data[0].CodigoCoelsa || "Sin registros";
  };
  
  return {
    // state
    selected,
    form,
    formType,
    afectacionesPatrimoniales,
    isLoading,
    isFormValid,
    validationErrors,

    // actions
    select,
    hydrateFormFromDoc,
    resetForm,
    clear,
    startCreate,
    startEdit,
    handleSave,
    fetchAfectaciones,
    handleCreateAfectacion,
    handleDeleteAfectacion,
    handleFetchCoelsa,
    handlePutAfectaciones,
    handleSaveLocally,
    startView,
    setShowSuccessModal,
    triggerSuccessModal,
    selectFirst,
    validateForm,
    setValidationErrors,

    // getters
    selectedId,
    isSelected,
    isCreate,
    isEdit,
    isView,
    inputsDisabled,
    isFormValid,
  };
});
